import { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { massMigrationApi } from '@/lib/api/massMigration';
import type { DetectedModuleDto } from '@/types/massMigration';
import {
  useMassMigrationWizardParsedCsv,
  useMassMigrationWizardExcludedRows,
  useMassMigrationWizardSelections,
  useMassMigrationWizardDetectedModules,
  useMassMigrationWizardUserMigrationRows,
  useMassMigrationWizardActions,
} from '@/stores/massMigrationWizardStore';
import { WizardNavigation } from './WizardNavigation';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Derive a readable display name from a camelCase or PascalCase moduleKey. */
function formatModuleDisplayName(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').trim();
}

// ─── ModuleSelectionStep ──────────────────────────────────────────────────────

export function ModuleSelectionStep() {
  const parsedCsv = useMassMigrationWizardParsedCsv();
  const excludedRows = useMassMigrationWizardExcludedRows();
  const selectionsByRow = useMassMigrationWizardSelections();
  const userMigrationEnabledRows = useMassMigrationWizardUserMigrationRows();
  const { setDetectedModules, toggleModuleForRow, toggleUserMigration, setUserMigrationForRows, setStep } =
    useMassMigrationWizardActions();
  const storedDetectedByRow = useMassMigrationWizardDetectedModules();

  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  // Active (non-excluded) rows
  const activeRows = useMemo(
    () => (parsedCsv?.rows ?? []).filter((r) => !excludedRows.includes(r.rowNumber)),
    [parsedCsv, excludedRows],
  );

  // Map rowNumber → bridgeId for the API call
  const rowsForDetect = useMemo(
    () => activeRows.map((r) => ({ rowNumber: r.rowNumber, bridgeId: r.bridgeId })),
    [activeRows],
  );

  const bridgeIds = useMemo(() => activeRows.map((r) => r.bridgeId), [activeRows]);

  const detectQueryKey = ['mass-migration', 'detect-modules', ...bridgeIds] as const;

  const detectQuery = useQuery({
    queryKey: detectQueryKey,
    queryFn: () => massMigrationApi.detectModules(rowsForDetect),
    enabled: Object.keys(selectionsByRow).length === 0 && bridgeIds.length > 0,
    staleTime: Infinity,
  });

  // Sync query result into store; also track detected modules for column display
  const [detectedByRow, setDetectedByRow] = useState<Record<number, DetectedModuleDto[]>>(() => {
    // On revisit, restore from store so columns remain visible
    return Object.keys(storedDetectedByRow).length > 0 ? storedDetectedByRow : {};
  });

  useEffect(() => {
    if (detectQuery.data !== undefined) {
      setDetectedByRow(detectQuery.data.byRow);
      setDetectedModules(detectQuery.data);
    }
  }, [detectQuery.data, setDetectedModules]);

  // Re-run: clear store selections then invalidate so the query re-fires
  const handleRerun = useCallback(() => {
    setDetectedModules({ byRow: {} });
    setDetectedByRow({});
    void queryClient.invalidateQueries({ queryKey: ['mass-migration', 'detect-modules'] });
  }, [queryClient, setDetectedModules]);

  // Collect all unique module keys across all orgs
  const allModuleKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.values(detectedByRow).forEach((modules) => modules.forEach((m) => keys.add(m.moduleKey)));
    return [...keys].sort();
  }, [detectedByRow]);

  // Filter rows by search
  const filteredRows = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (q === '') return activeRows;
    return activeRows.filter((r) => {
      const name = r.orgName?.toLowerCase() ?? '';
      const id = r.bridgeId.toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [activeRows, search]);

  const allUserMigrationEnabled =
    activeRows.length > 0 &&
    activeRows.every((r) => userMigrationEnabledRows.includes(r.rowNumber));

  // Stats
  const totalSelected = useMemo(() => {
    return Object.values(selectionsByRow).reduce((sum, arr) => sum + arr.length, 0);
  }, [selectionsByRow]);

  const totalAvailable = useMemo(() => {
    return Object.values(detectedByRow).reduce((sum, modules) => sum + modules.length, 0);
  }, [detectedByRow]);

  // Toggle entire column (all orgs for a given module)
  const toggleColumn = useCallback(
    (moduleKey: string) => {
      activeRows.forEach((r) => {
        const available = (detectedByRow[r.rowNumber] ?? []).map((m) => m.moduleKey);
        if (available.includes(moduleKey)) {
          toggleModuleForRow(r.rowNumber, moduleKey);
        }
      });
    },
    [activeRows, detectedByRow, toggleModuleForRow],
  );

  // Toggle all modules for a row (Total cell click)
  const toggleRowAll = useCallback(
    (rowNumber: number) => {
      const available = (detectedByRow[rowNumber] ?? []).map((m) => m.moduleKey);
      available.forEach((key) => toggleModuleForRow(rowNumber, key));
    },
    [detectedByRow, toggleModuleForRow],
  );

  const isLoading = detectQuery.isFetching;
  const hasDetected = allModuleKeys.length > 0;

  // canProceed guard: at least one module selected across all rows — Fix 4
  const canProceed = totalSelected > 0;

  return (
    <div className="space-y-6">
      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border p-12 text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Detecting modules…</p>
        </div>
      )}

      {/* Query error */}
      {detectQuery.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Module detection failed</AlertTitle>
          <AlertDescription>
            {detectQuery.error instanceof Error
              ? detectQuery.error.message
              : 'An unexpected error occurred.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Matrix */}
      {hasDetected && !isLoading && (
        <>
          {/* Header controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="org-search" className="sr-only">
                Search organizations
              </Label>
              <Input
                id="org-search"
                type="search"
                placeholder="Search organizations…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{totalSelected}</span> selected /{' '}
                {totalAvailable} available
              </p>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={handleRerun}
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Re-run
              </Button>
            </div>
          </div>

          {/* Org × Module matrix */}
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted">
                <tr>
                  {/* Org column */}
                  <th className="min-w-[200px] px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    Organization
                  </th>

                  {/* Module columns — Fix 6: aria-label + aria-pressed on toggle buttons */}
                  {allModuleKeys.map((key) => {
                    const allOrgsSelected = activeRows.every((r) => {
                      const available = (detectedByRow[r.rowNumber] ?? []).map((m) => m.moduleKey);
                      if (!available.includes(key)) return true; // not applicable, skip
                      return (selectionsByRow[r.rowNumber] ?? []).includes(key);
                    });
                    return (
                      <th
                        key={key}
                        className="px-3 py-2 text-center text-xs font-medium text-muted-foreground"
                      >
                        <button
                          type="button"
                          onClick={() => toggleColumn(key)}
                          className="whitespace-nowrap hover:text-foreground transition-colors"
                          aria-label={`Toggle ${key} for all organizations`}
                          aria-pressed={allOrgsSelected}
                        >
                          {formatModuleDisplayName(key)}
                        </button>
                      </th>
                    );
                  })}

                  {/* Total column */}
                  <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground">
                    Total
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row) => {
                  const availableModules = detectedByRow[row.rowNumber] ?? [];
                  const available = availableModules.map((m) => m.moduleKey);
                  const selected = selectionsByRow[row.rowNumber] ?? [];
                  const selectedCount = selected.length;
                  const orgName = row.orgName ?? row.bridgeId;

                  return (
                    <tr key={row.rowNumber} className="border-t border-border bg-background">
                      {/* Org cell */}
                      <td className="sticky left-0 bg-background px-3 py-2">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {row.orgName ?? '—'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            #{row.rowNumber} · {row.bridgeId}
                          </p>
                        </div>
                      </td>

                      {/* Module checkboxes */}
                      {allModuleKeys.map((key) => {
                        const isAvailable = available.includes(key);
                        const isChecked = selected.includes(key);
                        return (
                          <td key={key} className="px-3 py-2 text-center">
                            {isAvailable ? (
                              <Checkbox
                                id={`module-${row.rowNumber}-${key}`}
                                checked={isChecked}
                                onCheckedChange={() => toggleModuleForRow(row.rowNumber, key)}
                                aria-label={`${key} for row ${row.rowNumber}`}
                              />
                            ) : (
                              <span
                                className="text-muted-foreground"
                                aria-label="Not available"
                              >
                                —
                              </span>
                            )}
                          </td>
                        );
                      })}

                      {/* Total cell — Fix 6: aria-label + aria-pressed on row toggle */}
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => toggleRowAll(row.rowNumber)}
                          className={cn(
                            'rounded px-2 py-0.5 text-xs font-medium transition-colors',
                            selectedCount > 0
                              ? 'text-primary hover:text-primary/80'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                          aria-label={`Toggle all modules for ${orgName}`}
                          aria-pressed={selectedCount > 0 && selectedCount === available.length}
                        >
                          {selectedCount}
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {filteredRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={allModuleKeys.length + 2}
                      className="px-3 py-8 text-center text-sm text-muted-foreground"
                    >
                      No organizations match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* User Migration section */}
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                User Migration{' '}
                <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                  Phase 2
                </span>
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Enable user migration per organization. This is a separate phase from module
                migration.
              </p>
            </div>

            <div className="rounded-md border border-border divide-y divide-border">
              <div className="flex items-center justify-between bg-muted px-4 py-2">
                <p className="text-xs font-medium text-muted-foreground">
                  All organizations ({activeRows.length})
                </p>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="user-migration-all"
                    className="text-xs text-muted-foreground cursor-pointer"
                  >
                    {allUserMigrationEnabled ? 'Disable all' : 'Enable all'}
                  </Label>
                  <Switch
                    id="user-migration-all"
                    checked={allUserMigrationEnabled}
                    onCheckedChange={(checked) =>
                      setUserMigrationForRows(activeRows.map((r) => r.rowNumber), checked)
                    }
                    aria-label="User migration for all organizations"
                  />
                </div>
              </div>
              {filteredRows.map((row) => {
                const isEnabled = userMigrationEnabledRows.includes(row.rowNumber);
                return (
                  <div
                    key={row.rowNumber}
                    className="flex items-center justify-between px-4 py-2"
                  >
                    <div>
                      <p className="text-sm text-foreground">
                        {row.orgName ?? '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">{row.bridgeId}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor={`user-migration-${row.rowNumber}`}
                        className="text-xs text-muted-foreground cursor-pointer"
                      >
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </Label>
                      <Switch
                        id={`user-migration-${row.rowNumber}`}
                        checked={isEnabled}
                        onCheckedChange={() => toggleUserMigration(row.rowNumber)}
                        aria-label={`User migration for ${row.orgName ?? row.bridgeId}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Fix 4: disable Next when nothing is selected */}
      <WizardNavigation
        onBack={() => setStep(2)}
        onNext={canProceed ? () => setStep(4) : undefined}
        nextDisabled={!canProceed}
        nextLabel="Configure"
      />
    </div>
  );
}
